import type { ComponentProps, ReactNode } from 'react';
import { styled } from 'styled-components';

import type { ApiFilestackImageData } from '#/api/types/ApiFilestackImageData';
import { Form } from '#/components/Form/Form';
import type { FormSubmitFunction } from '#/components/Form/FormContext';
import { InputField } from '#/components/Form/InputField';
import { SubmitButton } from '#/components/Form/SubmitButton/SubmitButton';
import { Body } from '#/components/Text/Body';
import { H2 } from '#/components/Text/H2';
import { useAppConfig } from '#/config/AppConfigContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import { colors } from '#/theme/theme';
import { imageUrl } from '#/utils/filestack';
import { getStaticImageSrc } from '#/utils/getImageSrc';

import {
  CmsEmailCampaignSignup_Box_1,
  CmsEmailCampaignSignup_Box_2,
  CmsEmailCampaignSignup_Box_3,
} from './CmsEmailCampaignSignup.styled';

function StyledInputField({
  name,
  type,
  label,
  disabled,
}: {
  name: string;
  type?: 'text' | 'tel' | 'email' | 'number' | 'password' | 'url';
  label: string;
  disabled?: boolean;
}) {
  return (
    <InputField
      name={name}
      type={type}
      label={label}
      inlineLabel
      disabled={disabled}
      // @ts-expect-error TS(2322): Type '{ name: string; type: string; label: string;... Remove this comment to see the full error message
      borderRadius="0"
      border="none"
      height="60px"
      pt="16px"
      pb="10px"
    />
  );
}

const StyledSubmitButton = styled(
  ({ ...props }: ComponentProps<typeof SubmitButton>) => (
    // TODO: Fix this the next time the file is edited.
    // oxlint-disable-next-line react/jsx-props-no-spreading
    <SubmitButton {...props} />
  ),
)`
  max-width: 440px;
  box-shadow: 0 0 0 1px white inset;
  background: none;
  color: white;

  &:focus,
  &:hover {
    box-shadow: 0 0 0 1px ${colors.brandGreen} inset;
    background: ${colors.brandGreen};
  }
`;

export type CmsEmailSignupFormValues = {
  firstName?: string;
  lastName?: string;
  email?: string;
};

type Props = {
  headline: string;
  bgImage: ApiFilestackImageData | null | undefined;
  buttonText: string;
  disabled: boolean;
  isSubmitted: boolean;
  onSubmit: FormSubmitFunction<CmsEmailSignupFormValues>;
  qaId?: string;
  preForm?: ReactNode;
};

export function CmsEmailCampaignSignup({
  headline,
  bgImage,
  buttonText,
  disabled,
  isSubmitted,
  onSubmit,
  qaId,
  preForm,
}: Props) {
  const { appConfig } = useAppConfig();
  const { i18n } = useI18n();

  return (
    <div>
      <CmsEmailCampaignSignup_Box_3
        style={{
          background: `url("${
            bgImage
              ? imageUrl({
                  data: bgImage,
                  width: undefined,
                  height: undefined,
                  fit: 'max',
                  insecureSVG: false,
                  appConfig,
                })
              : getStaticImageSrc('blue-loops.svg')
          }") center/cover`,
        }}
      >
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          <H2 style={{ color: colors.white, textAlign: 'center' }}>
            {headline}
          </H2>
        </div>
        <Form
          analyticsTitle="[cms] Email campaign signup block"
          usageContext="page"
          qaId={qaId}
          onSubmit={onSubmit}
          render={() =>
            isSubmitted ? (
              <Body
                style={{
                  color: colors.white,
                  textAlign: 'center',
                  marginTop: '20px',
                }}
              >
                {i18n('Thank you for signing up!')}
              </Body>
            ) : (
              <>
                <div style={{ maxWidth: '440px', margin: '40px auto 0' }}>
                  <div style={{ marginBottom: '24px' }}>{preForm}</div>
                  <div style={{ overflow: 'hidden', borderRadius: '4px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                      <CmsEmailCampaignSignup_Box_2>
                        <StyledInputField
                          name="firstName"
                          type="text"
                          label={i18n('First name')}
                          disabled={disabled}
                        />
                      </CmsEmailCampaignSignup_Box_2>
                      <CmsEmailCampaignSignup_Box_1>
                        <StyledInputField
                          name="lastName"
                          type="text"
                          label={i18n('Last name')}
                          disabled={disabled}
                        />
                      </CmsEmailCampaignSignup_Box_1>
                    </div>
                    <div
                      style={{ borderTop: `1px solid ${colors.elementGrey}` }}
                    >
                      <StyledInputField
                        name="email"
                        type="email"
                        label={`${i18n('Email')}*`}
                        disabled={disabled}
                      />
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: '40px',
                  }}
                >
                  <StyledSubmitButton disabled={disabled}>
                    {buttonText}
                  </StyledSubmitButton>
                </div>
              </>
            )
          }
        />
      </CmsEmailCampaignSignup_Box_3>
    </div>
  );
}
